import React from "react";
import { ColumnsType } from "antd/es/table";
import { TFunction } from "i18next";
import clsx from "clsx";
import Input from "~/shared/ui/Fields/Input";
import Button from "~/shared/ui/Fields/Button";
import IconCheck from "~/components/Icons/icon-check-mark";
import IconCross from "~/components/Icons/icon-cross";
import IconPng from "~/components/Icons/mimeType/icon-png";
import IconJpeg from "~/components/Icons/mimeType/icon-jpeg";
import IconZip from "~/components/Icons/mimeType/icon-zip";
import IconBmp from "~/components/Icons/mimeType/icon-bmp";
import IconCsv from "~/components/Icons/mimeType/icon-csv";
import IconDoc from "~/components/Icons/mimeType/icon-doc";
import IconGif from "~/components/Icons/mimeType/icon-gif";
import IconGzip from "~/components/Icons/mimeType/icon-gzip";
import IconPpt from "~/components/Icons/mimeType/icon-ppt";
import IconRar from "~/components/Icons/mimeType/icon-rar";
import IconSvg from "~/components/Icons/mimeType/icon-svg";
import IconTxt from "~/components/Icons/mimeType/icon-txt";
import IconWebp from "~/components/Icons/mimeType/icon-webp";
import IconWbmp from "~/components/Icons/mimeType/icon-wbmp";
import IconTiff from "~/components/Icons/mimeType/icon-tiff";
import IconXls from "~/components/Icons/mimeType/icon-xls";
import Icon7zip from "~/components/Icons/mimeType/icon-7zip";
import IconPdf from "~/components/Icons/mimeType/icon-pdf";
import IconUnknow from "~/components/Icons/mimeType/icon-unknow-file";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import s from "~/components/Table/TableRow/PKDTableRow/TableRow.less";

const getExtension = (link: string) => /(?:\.([^.]+))?$/.exec(link)?.[1];

export const getMimeIcon = (mimeType: string, link: string) => {
  const point = getExtension(link);
  switch (mimeType) {
    case "image/png":
      return <IconPng />;
    case "image/jpeg":
    case "image/pjpeg":
      return <IconJpeg />;
    case "image/bmp":
    case "image/x-windows-bmp":
      return <IconBmp />;
    case "image/csv":
      return <IconCsv />;
    case "image/webp":
      return <IconWebp />;
    case "image/vnd.wap.wbmp":
      return <IconWbmp />;
    case "image/gif":
      return <IconGif />;
    case "application/pdf":
      return <IconPdf />;
    case "image/svg+xml":
      return <IconSvg />;
    case "image/tiff":
      return <IconTiff />;
    case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    case "application/msword":
      return <IconDoc />;
    case "application/vnd.ms-excel":
    case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
      return <IconXls />;
    case "application/zip":
    case "application/x-zip-compressed":
      return <IconZip />;
    case "application/gzip":
      return <IconGzip />;
    case "application/x-rar-compressed":
    case "application/vnd.rar":
    case "application/x-rar":
      return <IconRar />;
    case "application/vnd.ms-powerpoint":
    case "application/vnd.openxmlformats-officedocument.presentationml.presentation":
      return <IconPpt />;
    case "application/x-7z-compressed":
      return <Icon7zip />;
    case "text/plain":
      return <IconTxt />;
    case "application/octet-stream":
      if (point === "rar") return <IconRar />;
      if (point === "wbmp") return <IconWbmp />;
      return <IconUnknow />;
    default:
      return <IconUnknow />;
  }
};

const NameCell = ({
  value,
  record,
  control,
  handleSubmit,
  onSubmit,
}: {
  value: string;
  record: any;
  control: any;
  handleSubmit: any;
  onSubmit: any;
}) => {
  const setData = useSetLocationData();
  const { openModal, currentId } = useGetLocationData();

  const startEdit = () => setData("openModal", "edit", "currentId", record.id);
  const onCrossClick = () => setData("openModal", "", "currentId", "");

  const isEditing = openModal === "edit" && currentId === record.id;

  return (
    <span className={s.cont}>
      <span onDoubleClick={startEdit} className={s.mimeType}>
        {getMimeIcon(record.mimeType, record.link)}
      </span>
      {isEditing ? (
        <>
          <form className={s.form} onSubmit={handleSubmit(onSubmit)}>
            <Input
              name="name"
              control={control}
              maxLength={255}
              defaultValue={value}
            />
            <Button customClassName={s.save_changes} txtBtn="" onlyIcon isForm>
              <IconCheck fill="#000" />
            </Button>
          </form>
          <Button
            customClassName={s.cross_changes}
            txtBtn=""
            onlyIcon
            isForm
            onClick={onCrossClick}
          >
            <IconCross fill="#000" />
          </Button>
        </>
      ) : (
        <span className={clsx({ [s.nameRow]: true })}>{value}</span>
      )}
    </span>
  );
};

export const getPKDFilesColumns = (
  t: TFunction,
  control: any,
  handleSubmit: any,
  onSubmit: any
): ColumnsType<any> => [
  {
    title: t("pkd.name"),
    dataIndex: "name",
    key: "name",
    render: (value: string, record: any) => (
      <NameCell
        value={value}
        record={record}
        control={control}
        handleSubmit={handleSubmit}
        onSubmit={onSubmit}
      />
    ),
  },
  {
    title: t("pkd.createdAt"),
    dataIndex: "createdAt",
    key: "createdAt",
  },
  {
    title: t("pkd.fullname"),
    dataIndex: "fullname",
    key: "fullname",
  },
];
